import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TableMetaData } from '../_models/components/table-meta-data.model';
import { TableColumns } from '../_models/components/table-columns.model';
import { ActiveFilter } from '../_models/components/table-filter.model';

export interface ShareTablePreferences {
  visibleColumns: string[];
  rowsPerPage: number;
  sortField?: string;
  sortOrder?: number;
  globalFilter?: string;
  activeFilters?: ActiveFilter[];
  filterLogic?: 'AND' | 'OR';
}

export interface ShareTableInstance {
  id: string;
  preferences: ShareTablePreferences;
  metadata: TableMetaData;
  columns: TableColumns[];
}

@Injectable({
  providedIn: 'root'
})
export class ShareTableService {
  private readonly STORAGE_PREFIX = 'share_table_prefs_';
  private readonly DEFAULT_ROWS_PER_PAGE = 100;

  private tableInstances = new Map<string, ShareTableInstance>();
  private tableInstances$ = new BehaviorSubject<Map<string, ShareTableInstance>>(new Map());

  constructor() {
    this.restoreFromStorage();
  }

  private restoreFromStorage(): void {
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith(this.STORAGE_PREFIX))
        .forEach((key) => {
          const tableId = key.replace(this.STORAGE_PREFIX, '');
          const preferences = this.loadPreferences(tableId);
          const instance: ShareTableInstance = {
            id: tableId,
            preferences,
            metadata: this.metadataFromPreferences(preferences),
            columns: []
          };
          this.tableInstances.set(tableId, instance);
        });
      this.tableInstances$.next(this.tableInstances);
    } catch {}
  }

  private metadataFromPreferences(prefs: ShareTablePreferences): TableMetaData {
    return {
      totalRecords: 0,
      rows: prefs.rowsPerPage || this.DEFAULT_ROWS_PER_PAGE,
      first: 0,
      loading: false,
      globalFilter: prefs.globalFilter || '',
      sort: prefs.sortField
        ? `${prefs.sortField}:${prefs.sortOrder ?? 1}`
        : '',
      filter: (prefs.activeFilters ?? []).map((f) => f.field)
    };
  }

  registerTable(tableId: string, columns: TableColumns[]): ShareTableInstance {
    const existing = this.tableInstances.get(tableId);
    if (existing) {
      existing.columns = columns;
      if (!existing.preferences.visibleColumns?.length) {
        existing.preferences.visibleColumns = columns.map((c) => c.field);
        this.savePreferences(tableId, existing.preferences);
      }
      this.tableInstances$.next(this.tableInstances);
      return existing;
    }

    const preferences = this.loadPreferences(tableId);
    if (!preferences.visibleColumns?.length) {
      preferences.visibleColumns = columns.map((c) => c.field);
    }

    const instance: ShareTableInstance = {
      id: tableId,
      preferences,
      metadata: this.metadataFromPreferences(preferences),
      columns
    };

    this.tableInstances.set(tableId, instance);
    this.tableInstances$.next(this.tableInstances);
    return instance;
  }

  getTableInstance(tableId: string): ShareTableInstance | undefined {
    return this.tableInstances.get(tableId);
  }

  updatePreferences(tableId: string, preferences: Partial<ShareTablePreferences>): void {
    const instance = this.tableInstances.get(tableId);
    if (!instance) return;
    instance.preferences = { ...instance.preferences, ...preferences };
    this.savePreferences(tableId, instance.preferences);
    this.tableInstances$.next(this.tableInstances);
  }

  updateMetadata(tableId: string, metadata: Partial<TableMetaData>): void {
    const instance = this.tableInstances.get(tableId);
    if (!instance) return;
    instance.metadata = { ...instance.metadata, ...metadata };
    this.tableInstances$.next(this.tableInstances);
  }

  updateColumns(tableId: string, columns: TableColumns[]): void {
    const instance = this.tableInstances.get(tableId);
    if (!instance) return;
    instance.columns = columns;
    if (!instance.preferences.visibleColumns?.length) {
      instance.preferences.visibleColumns = columns.map((c) => c.field);
      this.savePreferences(tableId, instance.preferences);
    }
    this.tableInstances$.next(this.tableInstances);
  }

  getPreferences(tableId: string): ShareTablePreferences | null {
    // Check in-memory first; if the instance was unregistered (e.g. component
    // destroyed by a refresh), fall back to localStorage so the setter can still
    // apply the saved globalFilter before ngOnInit re-registers the table.
    return this.tableInstances.get(tableId)?.preferences ?? this.loadPreferences(tableId);
  }

  getMetadata(tableId: string): TableMetaData | null {
    return this.tableInstances.get(tableId)?.metadata ?? null;
  }

  getColumns(tableId: string): TableColumns[] | null {
    return this.tableInstances.get(tableId)?.columns ?? null;
  }

  getTableInstances$(): Observable<Map<string, ShareTableInstance>> {
    return this.tableInstances$.asObservable();
  }

  /** Removes the in-memory instance but keeps localStorage so prefs survive navigation. */
  unregisterTable(tableId: string): void {
    this.tableInstances.delete(tableId);
    this.tableInstances$.next(this.tableInstances);
  }

  clearAll(): void {
    this.tableInstances.clear();
    this.tableInstances$.next(this.tableInstances);
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith(this.STORAGE_PREFIX))
        .forEach((k) => localStorage.removeItem(k));
    } catch {}
  }

  generateTableId(context: string, entity?: string, id?: string): string {
    return [context, entity, id].filter(Boolean).join('_');
  }

  clearGlobalFiltersForContext(context: string): void {
    this.tableInstances.forEach((instance, tableId) => {
      if (tableId.startsWith(context)) {
        instance.preferences.globalFilter = '';
        instance.metadata.globalFilter = '';
        this.savePreferences(tableId, instance.preferences);
      }
    });
    this.tableInstances$.next(this.tableInstances);
  }

  preserveTableState(tableId: string): ShareTablePreferences | null {
    const instance = this.tableInstances.get(tableId);
    return instance ? { ...instance.preferences } : null;
  }

  restoreTableState(tableId: string, state: ShareTablePreferences): void {
    const instance = this.tableInstances.get(tableId);
    if (!instance) return;
    instance.preferences = { ...state };
    this.savePreferences(tableId, instance.preferences);
    this.tableInstances$.next(this.tableInstances);
  }

  private loadPreferences(tableId: string): ShareTablePreferences {
    try {
      const stored = localStorage.getItem(this.STORAGE_PREFIX + tableId);
      if (stored) return JSON.parse(stored) as ShareTablePreferences;
    } catch {}
    return {
      visibleColumns: [],
      rowsPerPage: this.DEFAULT_ROWS_PER_PAGE,
      globalFilter: '',
      activeFilters: [],
      filterLogic: 'AND'
    };
  }

  private savePreferences(tableId: string, preferences: ShareTablePreferences): void {
    try {
      localStorage.setItem(this.STORAGE_PREFIX + tableId, JSON.stringify(preferences));
    } catch {}
  }
}
